import {
  ApplicationConfig,
  importProvidersFrom,
  inject,
  provideAppInitializer,
  provideBrowserGlobalErrorListeners,
  provideZoneChangeDetection,
} from '@angular/core';
import { Router, provideRouter } from '@angular/router';
import {
  HTTP_INTERCEPTORS,
  HttpErrorResponse,
  provideHttpClient,
  withFetch,
  withInterceptors,
  withInterceptorsFromDi,
} from '@angular/common/http';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideNativeDateAdapter } from '@angular/material/core';
import {
  MSAL_INSTANCE,
  MSAL_INTERCEPTOR_CONFIG,
  MSAL_GUARD_CONFIG,
  MsalGuard,
  MsalBroadcastService,
  MsalInterceptor,
  MsalInterceptorConfiguration,
  MsalGuardConfiguration,
  MsalService,
} from '@azure/msal-angular';
import {
  InteractionType,
  IPublicClientApplication,
  PublicClientApplication,
} from '@azure/msal-browser';
import { environment } from '../environments/environment';
import { appRoutes } from './app.routes';
import { CurrentUserService } from './services/current-user.service';
import { accessDeniedInterceptor } from './interceptors/access-denied.interceptor';

function msalInstanceFactory(): IPublicClientApplication {
  return new PublicClientApplication({
    auth: {
      clientId: environment.auth.clientId,
      authority: `https://login.microsoftonline.com/${environment.auth.tenantId}`,
      redirectUri: environment.auth.redirectUri,
      postLogoutRedirectUri: environment.auth.postLogoutRedirectUri,
    },
    cache: {
      cacheLocation: 'localStorage',
      storeAuthStateInCookie: true,
    },
  });
}

function msalInterceptorConfigFactory(): MsalInterceptorConfiguration {
  const protectedResourceMap = new Map<string, Array<string>>();
  protectedResourceMap.set(environment.apiBaseUrl, environment.auth.scopes);
  protectedResourceMap.set(
    `${environment.apiBaseUrl}/*`,
    environment.auth.scopes
  );

  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap,
  };
}

function msalGuardConfigFactory(): MsalGuardConfiguration {
  return {
    interactionType: InteractionType.Redirect,
    authRequest: {
      scopes: environment.auth.scopes,
    },
  };
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(appRoutes),
    provideHttpClient(
      withFetch(),
      withInterceptorsFromDi(),
      withInterceptors([accessDeniedInterceptor])
    ),
    provideAnimations(),
    provideNativeDateAdapter(),
    importProvidersFrom(),
    { provide: MSAL_INSTANCE, useFactory: msalInstanceFactory },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: msalInterceptorConfigFactory,
    },
    { provide: MSAL_GUARD_CONFIG, useFactory: msalGuardConfigFactory },
    MsalGuard,
    { provide: HTTP_INTERCEPTORS, useClass: MsalInterceptor, multi: true },
    MsalService,
    MsalBroadcastService,
    // Load the current user's Access row on app startup so route guards can
    // read isAdministrator synchronously. Only redirect to /access-denied when
    // the API explicitly says the user isn't authorised (403 + marker). Other
    // failures (network down, VPN off, 500, 401) are swallowed so a transient
    // outage doesn't evict a valid user from the app.
    provideAppInitializer(async () => {
      const currentUser = inject(CurrentUserService);
      const router = inject(Router);
      const msal = inject(MsalService);
      await msal.instance.initialize();
      await msal.instance.handleRedirectPromise();
      if (msal.instance.getAllAccounts().length === 0) return;
      try {
        await currentUser.load();
      } catch (error) {
        if (
          error instanceof HttpErrorResponse &&
          error.status === 403 &&
          (
            (error.error as { message?: string } | null)?.message ?? ''
          ).includes('is not authorised for this application')
        ) {
          router.navigateByUrl('/access-denied');
        }
      }
    }),
  ],
};
